import safeStringify from 'fast-safe-stringify';

// only logs at this level or higher will be shown in production
const PRODUCTION_LOG_LEVEL_CUTOFF =
  Number(process.env.PRODUCTION_LOG_LEVEL_CUTOFF) || 2;

type LogLevelName =
  | 'error'
  | 'warn'
  | 'info'
  | 'http'
  | 'verbose'
  | 'debug'
  | 'silly';

type ConsoleMethodName = keyof Pick<
  Console,
  'error' | 'warn' | 'info' | 'log' | 'debug'
>;

interface LogLevel {
  std: ConsoleMethodName;
  level: number;
}
type LogLevels = Record<LogLevelName, LogLevel>;

// as defined in RFC5424
export const levels: LogLevels = {
  error: {
    std: 'error',
    level: 0,
  },
  warn: {
    std: 'warn',
    level: 1,
  },
  info: {
    std: 'info',
    level: 2,
  },
  http: {
    std: 'info',
    level: 3,
  },
  verbose: {
    std: 'log',
    level: 4,
  },
  debug: {
    std: 'debug',
    level: 5,
  },
  silly: {
    std: 'debug',
    level: 6,
  },
};

interface LoggerInstance {
  [levelName: string]: (..._args: any[]) => void;
}

const devEnvs = ['development', 'staging', 'local'];

// do not rely on production having a 'prod' env set
const isDevEnv = devEnvs.includes(
  process.env.NODE_ENV || process.env.NEXT_PUBLIC_APP_ENV || '',
);

export const createLogger = ({
  levels,
  verboseLogging,
}: {
  levels: LogLevels;
  verboseLogging: boolean;
}): LoggerInstance => {
  const loggerInstance: LoggerInstance = {};

  Object.entries(levels).forEach(([levelName, levelValue]) => {
    loggerInstance[levelName] = () => {};

    if (levelValue.level <= PRODUCTION_LOG_LEVEL_CUTOFF || verboseLogging) {
      loggerInstance[levelName] = (...args) => {
        if (!isDevEnv) {
          for (const arg of args) {
            if (typeof arg === 'object') {
              // stringify any objects such that the entire object can be displayed in a single stdout message on CloudWatch
              console[levelValue.std](safeStringify(arg));
            } else {
              console[levelValue.std](arg);
            }
          }
        } else {
          console[levelValue.std](...args);
        }
      };
    }
  });

  return loggerInstance;
};

export const logger = createLogger({
  levels,
  verboseLogging: isDevEnv,
});
